import { X } from "lucide-react";
import { useEffect, useState } from "react";

interface Props {
  open: boolean;
  onClose: () => void;
  onConfirm: (reason: string) => void | Promise<void>;
}

const REASON_MIN_LENGTH = 10;

export function UnlockBlueprintModal({ open, onClose, onConfirm }: Props) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) {
      setReason("");
      setBusy(false);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const trimmed = reason.trim();
  const valid = trimmed.length >= REASON_MIN_LENGTH;

  const handleConfirm = async () => {
    if (busy || !valid) return;
    setBusy(true);
    try {
      await onConfirm(trimmed);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="unlock-blueprint-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
    >
      <button
        type="button"
        aria-label="Close unlock blueprint dialog"
        className="absolute inset-0 h-full w-full cursor-default"
        onClick={onClose}
      />
      <div className="relative z-50 w-full max-w-lg rounded-lg bg-card p-5 shadow-xl">
        <div className="flex items-start justify-between">
          <div>
            <h3 id="unlock-blueprint-title" className="text-lg font-semibold text-text-primary">
              Unlock blueprint
            </h3>
            <p className="mt-2 text-sm text-text-muted">
              Unlocking returns the blueprint to draft. The current locked version is preserved and
              remains queryable. Downstream artefacts will be flagged out of sync after you re-lock
              with changes.
            </p>
          </div>
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            className="rounded p-1 text-text-faint hover:bg-card-hover"
          >
            <X size={16} />
          </button>
        </div>

        <div className="mt-4">
          <label
            htmlFor="unlock-reason"
            className="mb-1 block text-xs font-medium text-text-secondary"
          >
            Reason <span className="text-rose-500">*</span>
          </label>
          <textarea
            id="unlock-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            placeholder="Why are you unlocking this blueprint?"
            className="w-full rounded border border-border bg-background px-2 py-1.5 text-sm text-text-primary focus:border-accent focus:outline-none"
          />
          <p className="mt-1 text-xs text-text-faint">
            {trimmed.length < REASON_MIN_LENGTH
              ? `At least ${REASON_MIN_LENGTH} characters (${trimmed.length}/${REASON_MIN_LENGTH})`
              : `${trimmed.length} characters`}
          </p>
        </div>

        <div className="mt-4 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded px-3 py-1.5 text-sm text-text-secondary hover:bg-card-hover"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={busy || !valid}
            className="rounded bg-accent px-3 py-1.5 text-sm font-medium text-white hover:bg-accent/90 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Unlock blueprint
          </button>
        </div>
      </div>
    </div>
  );
}
